import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CircularProgress } from '../components/ui/CircularProgress';
import { LiveActivityFeed } from '../components/batch/LiveActivityFeed';
import { triggerCelebration, triggerStars } from '../components/ui/CelebrationEffects';
import {
  Upload, FileText, ChevronRight, ChevronLeft, X, Check, Play, Pause,
  Trophy, Medal, Sparkles, Zap, Clock, AlertCircle, CheckCircle2,
  Loader2, Download, BarChart3, TrendingUp, TrendingDown, Target,
  Brain, Lightbulb, Scale, Heart, Users, ArrowRight, FileUp, Archive, Home
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const pageVariants = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

const STEPS = [
  { id: 'upload', title: 'Upload', icon: Upload },
  { id: 'preview', title: 'Review', icon: FileText },
  { id: 'evaluate', title: 'Evaluate', icon: Sparkles },
  { id: 'results', title: 'Results', icon: Trophy }
];

const DIMENSIONS = [
  { key: 'summary', label: 'Analyzing Paper...', icon: FileText, color: 'slate' },
  { key: 'novelty', label: 'Novelty', icon: Lightbulb, color: 'purple' },
  { key: 'resonance', label: 'Resonance', icon: Heart, color: 'pink' },
  { key: 'synthesis', label: 'Synthesis', icon: Brain, color: 'blue' },
  { key: 'ethical', label: 'Ethical', icon: Scale, color: 'green' },
  { key: 'msa', label: 'Thinking Style', icon: Zap, color: 'orange' }
];

export default function BatchEvaluationWizard() {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);

  // Upload state
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Preview state
  const [filePreviews, setFilePreviews] = useState([]);
  const [timeEstimate, setTimeEstimate] = useState('');
  const [jobName, setJobName] = useState('');
  const [preparing, setPreparing] = useState(false);

  // Evaluation state
  const [jobId, setJobId] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPaper, setCurrentPaper] = useState(null);
  const [currentDimension, setCurrentDimension] = useState(0);
  const [dimensionResults, setDimensionResults] = useState({});
  const [completedEvaluations, setCompletedEvaluations] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [extractedContent, setExtractedContent] = useState('');

  // Results state
  const [analytics, setAnalytics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  // Live feed items
  const liveFeedItems = completedEvaluations.slice().reverse().map(e => ({
    id: e.student?.id || Math.random(),
    name: e.student?.name || e.filename,
    rollNumber: e.student?.roll_number || '-',
    score: e.evaluation?.oi_scores?.total || 0
  }));

  // Load cohort on mount
  useEffect(() => {
    const loadCohortData = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}`, { withCredentials: true });
        if (res.data.success) setCohort(res.data.cohort);
      } catch (e) {
        toast({ title: "Error", description: "Could not load cohort", variant: "destructive" });
        navigate('/educator');
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated && cohortId) {
      loadCohortData();
    }
  }, [isAuthenticated, cohortId, navigate, toast]);

  // Timer
  useEffect(() => {
    let interval;
    if (isEvaluating && startTime && !isPaused) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isEvaluating, startTime, isPaused]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  // File handling
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Prepare batch
  const handlePrepare = async () => {
    if (files.length === 0) return;
    
    setPreparing(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));

      const res = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/prepare-batch`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );

      if (res.data.success) {
        setFilePreviews(res.data.file_previews);
        setTimeEstimate(res.data.time_estimate);
        setCurrentStep(1);
      }
    } catch (e) {
      toast({ title: "Preparation Failed", description: e.response?.data?.detail || "Error", variant: "destructive" });
    } finally {
      setPreparing(false);
    }
  };

  // Start evaluation
  const startEvaluation = async () => {
    const name = jobName || `Batch ${new Date().toLocaleDateString()}`;
    
    try {
      const formData = new FormData();
      formData.append('job_name', name);
      formData.append('total_papers', filePreviews.length);

      const res = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/create-job`,
        formData,
        { withCredentials: true }
      );

      if (res.data.success) {
        setJobId(res.data.job.id);
        setCurrentStep(2);
        setIsEvaluating(true);
        setStartTime(Date.now());
        setCurrentIndex(0);
        evaluatePaperProgressive(0, res.data.job.id);
      }
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail || "Could not start", variant: "destructive" });
    }
  };

  // Progressive evaluation - dimension by dimension
  const evaluatePaperProgressive = async (index, jid) => {
    if (index >= filePreviews.length || isPaused) {
      if (index >= filePreviews.length) completeEvaluation(jid);
      return;
    }

    const fp = filePreviews[index];
    setCurrentIndex(index);
    setCurrentPaper({ ...fp, status: 'processing' });
    setCurrentDimension(0);
    setDimensionResults({});

    try {
      // Find file
      let fileToUpload = files.find(f => f.name === fp.filename);
      if (!fileToUpload && fp.from_zip) {
        fileToUpload = files.find(f => f.name === fp.from_zip);
      }
      if (!fileToUpload) throw new Error('File not found');

      // First, extract content
      const extractFormData = new FormData();
      extractFormData.append('file', fileToUpload);
      
      const extractRes = await axios.post(
        `${BACKEND_URL}/api/extract-text`,
        extractFormData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );

      if (!extractRes.data.success) {
        throw new Error(extractRes.data.error || 'Could not extract text');
      }

      const content = extractRes.data.text;
      const title = fp.filename.rsplit ? fp.filename.rsplit('.', 1)[0] : fp.filename.split('.')[0];
      setExtractedContent(content.slice(0, 500) + '...');

      // Evaluate each dimension progressively
      const results = {};
      const dimensionOrder = ['summary', 'novelty', 'resonance', 'synthesis', 'ethical', 'msa'];

      for (let i = 0; i < dimensionOrder.length; i++) {
        if (isPaused) break;
        
        const dim = dimensionOrder[i];
        setCurrentDimension(i);

        try {
          const dimRes = await axios.post(
            `${BACKEND_URL}/api/evaluate-paper-progressive`,
            { title, content: content.slice(0, 10000), dimension: dim },
            { withCredentials: true }
          );

          results[dim] = dimRes.data;
          setDimensionResults(prev => ({ ...prev, [dim]: dimRes.data }));
        } catch (dimError) {
          console.error(`Error evaluating ${dim}:`, dimError);
          results[dim] = { error: dimError.message };
        }
      }

      // Calculate final scores - use correct field names from progressive API
      const oi_scores = {
        novelty: results.novelty?.data?.novelty || 5,
        resonance: results.resonance?.data?.resonance || 5,
        synthesis: results.synthesis?.data?.synthesis || 5,
        ethical: results.ethical?.data?.ethical || 5,
      };
      oi_scores.total = (oi_scores.novelty + oi_scores.resonance + oi_scores.synthesis + oi_scores.ethical) / 4;

      const msa_scores = {
        spark_pct: results.msa?.data?.spark_percentage || 50,
        mirror_pct: results.msa?.data?.mirror_percentage || 50
      };

      // Save result
      const saveFormData = new FormData();
      saveFormData.append('job_id', jid);
      saveFormData.append('filename', fp.filename);
      saveFormData.append('file', fileToUpload);
      if (fp.matched_student) {
        saveFormData.append('student_id', fp.matched_student.id);
      } else {
        saveFormData.append('roll_number', fp.manual_roll || fp.auto_detected_roll);
        saveFormData.append('student_name', fp.manual_name || fp.auto_detected_name);
      }

      // Quick save without re-evaluation
      const saveRes = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/save-evaluation`,
        {
          job_id: jid,
          filename: fp.filename,
          student_id: fp.matched_student?.id,
          roll_number: fp.manual_roll || fp.auto_detected_roll,
          student_name: fp.manual_name || fp.auto_detected_name,
          oi_scores,
          msa_scores,
          summary: results.summary?.data?.summary || '',
          key_insights: results.novelty?.data?.key_findings || [],
          strengths: results.synthesis?.data?.strengths || [],
          weaknesses: results.synthesis?.data?.weaknesses || []
        },
        { withCredentials: true }
      );

      const finalResult = {
        ...fp,
        status: 'completed',
        evaluation: {
          oi_scores,
          msa_scores,
          summary: results.summary?.data?.summary,
          key_insights: results.novelty?.data?.key_findings || [],
          strengths: results.synthesis?.data?.strengths || [],
          weaknesses: results.synthesis?.data?.weaknesses || []
        },
        student: saveRes.data?.student || fp.matched_student
      };

      // Celebration for high scores!
      if (oi_scores.total >= 8) {
        triggerCelebration();
      }

      setCompletedEvaluations(prev => [...prev, finalResult]);
      setCurrentPaper(finalResult);

      if (!isPaused) {
        setTimeout(() => evaluatePaperProgressive(index + 1, jid), 500);
      }

    } catch (e) {
      console.error('Evaluation error:', e);
      const result = { ...fp, status: 'failed', error: e.message };
      setCompletedEvaluations(prev => [...prev, result]);
      setCurrentPaper(result);
      if (!isPaused) setTimeout(() => evaluatePaperProgressive(index + 1, jid), 500);
    }
  };

  // Complete evaluation
  const completeEvaluation = async (jid) => {
    setIsEvaluating(false);
    triggerStars();

    try {
      await axios.post(`${BACKEND_URL}/api/educator/jobs/${jid}/complete`, {}, { withCredentials: true });

      const [analyticsRes, leaderboardRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}/analytics`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}/leaderboard`, { withCredentials: true })
      ]);

      if (analyticsRes.data.success) setAnalytics(analyticsRes.data.analytics);
      if (leaderboardRes.data.success) setLeaderboard(leaderboardRes.data.leaderboard);

      setCurrentStep(3);
    } catch (e) {
      setCurrentStep(3);
    }
  };

  const togglePause = () => {
    if (isPaused) {
      setIsPaused(false);
      evaluatePaperProgressive(currentIndex + 1, jobId);
    } else {
      setIsPaused(true);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-purple-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/educator/cohort/${cohortId}`)} className="text-slate-600 hover:text-purple-600">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
                {cohort?.name}
              </h1>
              <p className="text-sm text-slate-500">Batch Evaluation</p>
            </div>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center gap-2">
            {STEPS.map((step, i) => (
              <div key={step.id} className="flex items-center">
                <motion.div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-sm ${
                    i === currentStep ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' :
                    i < currentStep ? 'bg-green-500 text-white' : 'bg-white border-2 border-slate-200 text-slate-400'
                  }`}
                  animate={{ scale: i === currentStep ? 1.1 : 1 }}
                >
                  {i < currentStep ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                </motion.div>
                {i < STEPS.length - 1 && (
                  <div className={`w-12 h-1 mx-1 rounded ${i < currentStep ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          <Button variant="ghost" size="sm" onClick={() => navigate(`/educator/cohort/${cohortId}`)} className="text-slate-600 hover:text-purple-600">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[calc(100vh-80px)]">
        {/* Left Panel - Main Content */}
        <div className="lg:col-span-8 xl:col-span-9 p-6 lg:p-10">
          <AnimatePresence mode="wait">
            {/* STEP 0: Upload */}
            {currentStep === 0 && (
              <motion.div key="upload" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }} className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="w-20 h-20 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                  >
                    <Upload className="w-10 h-10 text-white" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-slate-800 mb-2">Upload Papers</h2>
                  <p className="text-slate-500">Drag and drop files or click to browse</p>
                </div>

                <Card className={`border-2 border-dashed transition-all cursor-pointer ${isDragging ? 'border-purple-400 bg-purple-50' : 'border-slate-300 hover:border-purple-400'}`}>
                  <CardContent 
                    className="p-12 text-center"
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.xlsx,.pptx,.csv,.zip" multiple className="hidden" onChange={handleFileSelect} />
                    <FileUp className="w-16 h-16 mx-auto text-purple-400 mb-4" />
                    <p className="text-lg text-slate-700 mb-2">{isDragging ? 'Drop files here!' : 'Drop files or click to browse'}</p>
                    <p className="text-sm text-slate-400">PDF, Word, Excel, PowerPoint, CSV, or ZIP</p>
                  </CardContent>
                </Card>

                {files.length > 0 && (
                  <Card className="mt-6">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base">{files.length} files selected</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => setFiles([])} className="text-slate-500 hover:text-red-500">Clear</Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 max-h-48 overflow-y-auto space-y-2">
                      {files.map((file, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {file.name.endsWith('.zip') ? <Archive className="w-5 h-5 text-amber-500" /> : <FileText className="w-5 h-5 text-purple-500" />}
                            <span className="text-sm text-slate-700 truncate max-w-xs">{file.name}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="mt-8 flex justify-center">
                  <Button onClick={handlePrepare} disabled={files.length === 0 || preparing} className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg">
                    {preparing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Preparing...</> : <>Continue<ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 1: Preview */}
            {currentStep === 1 && (
              <motion.div key="preview" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-slate-800 mb-2">Review & Match</h2>
                  <p className="text-slate-500">Verify auto-matched students</p>
                </div>

                <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 mb-6">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-purple-800 font-semibold">Estimated: {timeEstimate}</p>
                        <p className="text-purple-600 text-sm">{filePreviews.length} papers • Progressive mode</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Input placeholder="Batch name" value={jobName} onChange={(e) => setJobName(e.target.value)} className="mb-6" />

                <Card className="mb-6">
                  <CardContent className="p-0 max-h-80 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">#</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Filename</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filePreviews.map((fp, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-4 py-3 text-sm text-slate-400">{i + 1}</td>
                            <td className="px-4 py-3 text-sm text-slate-700 truncate max-w-xs">{fp.filename}</td>
                            <td className="px-4 py-3"><Badge className={fp.status === 'matched' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{fp.status}</Badge></td>
                            <td className="px-4 py-3 text-sm text-slate-600">{fp.matched_student?.name || fp.auto_detected_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(0)}><ChevronLeft className="w-5 h-5 mr-2" />Back</Button>
                  <Button onClick={startEvaluation} className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white px-8">
                    <Play className="w-5 h-5 mr-2" />Start Evaluation
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Progressive Evaluation */}
            {currentStep === 2 && (
              <motion.div key="evaluate" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Evaluating Paper {currentIndex + 1} of {filePreviews.length}</h2>
                    <p className="text-slate-500">Elapsed: {formatTime(elapsedTime)}</p>
                  </div>
                  <Button variant="outline" onClick={togglePause} className={isPaused ? 'border-green-400 text-green-600' : 'border-amber-400 text-amber-600'}>
                    {isPaused ? <><Play className="w-4 h-4 mr-2" />Resume</> : <><Pause className="w-4 h-4 mr-2" />Pause</>}
                  </Button>
                </div>

                {/* Overall Progress */}
                <Card className="mb-6 border-purple-200">
                  <CardContent className="py-4">
                    <Progress value={(currentIndex / filePreviews.length) * 100} className="h-2 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>{completedEvaluations.filter(e => e.status === 'completed').length} completed</span>
                      <span>~{formatTime(Math.max(0, (filePreviews.length - currentIndex - 1) * 30))} remaining</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Current Paper Card with Progressive Dimensions */}
                {currentPaper && (
                  <Card className="border-2 border-purple-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-3">
                            {currentPaper.status === 'processing' && <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />}
                            {currentPaper.status === 'completed' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                            {currentPaper.status === 'failed' && <AlertCircle className="w-5 h-5 text-red-500" />}
                            {currentPaper.student?.name || currentPaper.auto_detected_name || currentPaper.filename}
                          </CardTitle>
                          <CardDescription>{currentPaper.filename}</CardDescription>
                        </div>
                        {dimensionResults.novelty && (
                          <div className="text-right">
                            <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                              {((
                                (dimensionResults.novelty?.data?.score || 0) +
                                (dimensionResults.resonance?.data?.score || 0) +
                                (dimensionResults.synthesis?.data?.score || 0) +
                                (dimensionResults.ethical?.data?.score || 0)
                              ) / 4).toFixed(1)}
                            </p>
                            <p className="text-sm text-slate-400">OI Score</p>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Progressive Dimension Cards */}
                      <div className="grid grid-cols-2 gap-3">
                        {DIMENSIONS.slice(1, 5).map((dim, i) => {
                          const result = dimensionResults[dim.key];
                          const isActive = currentDimension === i + 1;
                          const isComplete = result && !result.error;
                          const Icon = dim.icon;
                          
                          return (
                            <motion.div
                              key={dim.key}
                              initial={{ opacity: 0.5, scale: 0.95 }}
                              animate={{ 
                                opacity: isComplete || isActive ? 1 : 0.5,
                                scale: isActive ? 1.02 : 1,
                                borderColor: isActive ? '#8b5cf6' : isComplete ? '#22c55e' : '#e2e8f0'
                              }}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                isActive ? 'bg-purple-50 border-purple-400' :
                                isComplete ? 'bg-green-50 border-green-300' :
                                'bg-slate-50 border-slate-200'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Icon className={`w-5 h-5 ${isActive ? 'text-purple-600' : isComplete ? 'text-green-600' : 'text-slate-400'}`} />
                                  <span className={`font-medium ${isActive ? 'text-purple-700' : isComplete ? 'text-green-700' : 'text-slate-500'}`}>
                                    {dim.label}
                                  </span>
                                </div>
                                {isActive && <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />}
                                {isComplete && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                              </div>
                              
                              {isComplete && (
                                <motion.div
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                >
                                  <p className="text-3xl font-bold text-slate-800">{result.data?.score?.toFixed(1) || '-'}</p>
                                  {result.data?.reasoning && (
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{result.data.reasoning}</p>
                                  )}
                                </motion.div>
                              )}
                              
                              {!isComplete && !isActive && (
                                <p className="text-2xl font-bold text-slate-300">--</p>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* MSA Progress */}
                      {dimensionResults.msa && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-xl p-4 border border-orange-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="flex items-center gap-2 text-orange-600">
                              <Zap className="w-4 h-4" />
                              Spark {dimensionResults.msa.data?.spark_percentage?.toFixed(0) || 50}%
                            </span>
                            <span className="flex items-center gap-2 text-blue-600">
                              Mirror {dimensionResults.msa.data?.mirror_percentage?.toFixed(0) || 50}%
                              <Target className="w-4 h-4" />
                            </span>
                          </div>
                          <Progress value={dimensionResults.msa.data?.spark_percentage || 50} className="h-2" />
                        </motion.div>
                      )}

                      {/* Summary */}
                      {dimensionResults.summary?.data?.summary && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="bg-slate-50 rounded-xl p-4"
                        >
                          <p className="text-sm text-slate-600 flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                            {dimensionResults.summary.data.summary}
                          </p>
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}

            {/* STEP 3: Results */}
            {currentStep === 3 && (
              <motion.div key="results" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>
                <div className="text-center mb-8">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-24 h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Trophy className="w-12 h-12 text-white" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-slate-800 mb-2">Evaluation Complete!</h2>
                  <p className="text-slate-500">{completedEvaluations.filter(e => e.status === 'completed').length} papers in {formatTime(elapsedTime)}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-8">
                  <Card className="border-purple-200 bg-purple-50"><CardContent className="py-6 text-center"><p className="text-4xl font-bold text-purple-600">{analytics?.oi_score?.avg?.toFixed(1) || '-'}</p><p className="text-slate-500">Avg Score</p></CardContent></Card>
                  <Card className="border-green-200 bg-green-50"><CardContent className="py-6 text-center"><p className="text-4xl font-bold text-green-600">{analytics?.oi_score?.max?.toFixed(1) || '-'}</p><p className="text-slate-500">Highest</p></CardContent></Card>
                  <Card className="border-orange-200 bg-orange-50"><CardContent className="py-6 text-center"><p className="text-4xl font-bold text-orange-600">{analytics?.patterns?.avg_spark_pct?.toFixed(0) || 50}%</p><p className="text-slate-500">Spark</p></CardContent></Card>
                </div>

                <Card className="mb-8">
                  <CardHeader><CardTitle className="flex items-center gap-2"><Medal className="w-5 h-5 text-amber-500" />Top Performers</CardTitle></CardHeader>
                  <CardContent>
                    {leaderboard.slice(0, 5).map((student, i) => (
                      <div key={i} className={`flex items-center justify-between p-4 rounded-xl mb-2 ${i === 0 ? 'bg-amber-50 border-2 border-amber-200' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-4">
                          <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-orange-500' : 'bg-slate-300'}`}>{i + 1}</span>
                          <div><p className="font-semibold">{student.name}</p><p className="text-sm text-slate-500">{student.roll_number}</p></div>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">{student.oi_score?.toFixed(1)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <div className="flex justify-center gap-4">
                  <Button variant="outline"><Download className="w-5 h-5 mr-2" />Export</Button>
                  <Button onClick={() => navigate(`/educator/cohort/${cohortId}`)} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">Back to Cohort<ArrowRight className="w-5 h-5 ml-2" /></Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Panel */}
        <div className="lg:col-span-4 xl:col-span-3 bg-white/50 border-l border-purple-100 p-6 hidden lg:block">
          <div className="text-center mb-8">
            <CircularProgress value={completedEvaluations.length} max={Math.max(filePreviews.length, 1)} size={140} strokeWidth={10} label="completed" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-8">
            <Card className="border-green-200"><CardContent className="py-3 text-center"><CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-green-500" /><p className="text-lg font-bold">{completedEvaluations.filter(e => e.status === 'completed').length}</p><p className="text-xs text-slate-500">Success</p></CardContent></Card>
            <Card className="border-red-200"><CardContent className="py-3 text-center"><AlertCircle className="w-5 h-5 mx-auto mb-1 text-red-500" /><p className="text-lg font-bold">{completedEvaluations.filter(e => e.status === 'failed').length}</p><p className="text-xs text-slate-500">Failed</p></CardContent></Card>
          </div>
          <LiveActivityFeed items={liveFeedItems} maxItems={6} />
        </div>
      </div>
    </div>
  );
}
